import { Children, useState, useEffect, useRef } from 'react'
import styled from 'styled-components'
import { usePopper } from 'react-popper'
import { H5, ParagraphSmall } from '@/components/text'
import { breakpoints, colors } from '@/styles'
import Icon from '@/components/icon'
import useMediaQuery from '@/hooks/use-media-query'

type Color = 'primary' | 'secondary'

type TooltipProps = {
  children: React.ReactNode
  content: React.ReactNode
  title?: React.ReactNode
  openOnClick?: boolean
  size?: 'small' | 'medium'
  theme?: Color
  placement?:
    | 'top-start'
    | 'top-end'
    | 'top'
    | 'bottom-start'
    | 'bottom-end'
    | 'bottom'
    | 'left'
    | 'right'
  unfixedWidth?: boolean
  className?: string
}

type ContainerProps = {
  unfixedWidth: boolean
}

const getColor = (theme: Color) => {
  if (theme === 'secondary') return colors.grey16
  return colors.blue32
}

const Wrapper = styled.div`
  cursor: default;
  @media (max-width: ${breakpoints.tabletLandscape}) {
    gap: 0 8px;
    display: flex;
  }
`

const Button = styled.button`
  vertical-align: middle;
  display: inline-flex;
  background: none;
  border: 0;
  padding: 0;
  cursor: auto;
  @media (max-width: ${breakpoints.tabletLandscape}) {
    display: flex;
  }
`

const Arrow = styled.div`
  visibility: hidden;
  &,
  &:before {
    position: absolute;
    width: 14px;
    height: 14px;
    background: inherit;
  }
  &:before {
    visibility: visible;
    content: '';
    transform: rotate(45deg);
    background: var(--tooltip-color);
  }
`

const Popper = styled.div<{ theme?: Color }>`
  --tooltip-color: ${props => getColor(props.theme)};
  z-index: 999;
  box-shadow: 0px 10px 15px -3px rgba(0, 0, 0, 0.1), 0px 4px 6px -2px rgba(0, 0, 0, 0.05);
  border-radius: 8px;
  color: ${colors.white};
  background: var(--tooltip-color);
  padding: 16px;

  &[data-popper-placement^='top'] > ${Arrow} {
    bottom: -6px;
    &:before {
      border-bottom-right-radius: 3px;
    }
  }

  &[data-popper-placement^='bottom'] > ${Arrow} {
    top: -6px;
    &:before {
      border-top-left-radius: 3px;
    }
  }

  &[data-popper-placement^='left'] > ${Arrow} {
    right: -6px;
    &:before {
      border-top-right-radius: 3px;
    }
  }

  &[data-popper-placement^='right'] > ${Arrow} {
    left: -6px;
    &:before {
      border-bottom-left-radius: 3px;
    }
  }
`

const Container = styled.div<ContainerProps>`
  width: 100%;
  height: 100%;
  max-width: ${props => (props.unfixedWidth ? 'unset' : '348px')};
  display: flex;
  flex-direction: column;
  overflow: visible;
  text-overflow: clip;
  white-space: normal;
  gap: 6px 0;
  @media (max-width: ${breakpoints.ipadMiniMax}) {
    max-width: 304px;
  }

  a {
    color: ${colors.white};
    text-decoration: underline;
  }
`

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 0 24px;
`

const Title = styled(H5)`
  color: ${colors.white};
`

const Text = styled(ParagraphSmall)<{ size?: string }>`
  display: flex;
  align-items: center;
  color: ${colors.white};
  gap: 12px;

  ${props =>
    props.size === 'medium' &&
    `
    font-size: 14px;
    line-height: 16px;
  `}
`

const CloseButton = styled.button<{ showOnDesktop?: boolean }>`
  display: ${props => (props.showOnDesktop ? 'block' : 'none')};
  background: 0;
  border: 0;
  padding: 0;
  color: ${colors.grey80};
  cursor: pointer;
  @media (max-width: ${breakpoints.ipadMiniMax}) {
    display: block;
  }
`

type AnyEvent = MouseEvent | TouchEvent

const offset = ({ placement }: { placement: string }): [number, number] => {
  if (placement === 'top-start') return [-40, 12]
  if (placement === 'top-end') return [40, 12]
  if (placement === 'top') return [0, 12]
  if (placement === 'bottom-start') return [-40, 12]
  if (placement === 'bottom-end') return [40, 12]
  if (placement === 'bottom') return [0, 12]
  if (placement === 'left') return [0, 12]
  if (placement === 'right') return [0, 12]
  return [-40, 18]
}

const TooltipBase: React.FC<TooltipProps> = ({
  title,
  content,
  theme = 'primary',
  placement = 'top-start',
  size = 'small',
  openOnClick,
  children,
  unfixedWidth = false,
  className
}) => {
  const isMobile = useMediaQuery(`(max-width: ${breakpoints.phoneMax})`)
  const node = useRef<HTMLDivElement>(null)
  const [visible, setVisible] = useState(false)
  const [referenceElement, setReferenceElement] = useState<HTMLButtonElement | null>(null)
  const [popperElement, setPopperElement] = useState<HTMLDivElement | null>(null)
  const [arrowElement, setArrowElement] = useState<HTMLDivElement | null>(null)
  const { styles, attributes } = usePopper(referenceElement, popperElement, {
    placement: isMobile ? 'top' : placement,
    modifiers: [
      {
        name: 'offset',
        options: {
          offset
        }
      },
      {
        name: 'arrow',
        options: {
          element: arrowElement
        }
      },
      {
        name: 'preventOverflow',
        options: {
          padding: 8
        }
      }
    ]
  })
  const handleClick = (event: AnyEvent) => {
    if (node?.current?.contains(event.target as Node)) {
      return
    }
    setVisible(false)
  }

  useEffect(() => {
    if (typeof document === 'undefined') return
    document.addEventListener('mousedown', handleClick)
    return () => {
      document.removeEventListener('mousedown', handleClick)
    }
  }, [])
  // We want the last element to be where the tooltip
  // point is anchored.
  const count = Children.count(children)
  const reference = Children.map(children, (child, index) => {
    if (index + 1 >= count)
      return (
        <Button
          ref={setReferenceElement}
          type="button"
          onClick={() => setVisible(true)}
          onFocus={() => !openOnClick && setVisible(true)}
          onBlur={() => !openOnClick && setVisible(false)}
        >
          {child}
        </Button>
      )
    return child
  })
  return (
    <Wrapper
      ref={node}
      onMouseEnter={() => !openOnClick && setVisible(true)}
      onMouseLeave={() => !openOnClick && setVisible(false)}
      className={className}
    >
      {reference}
      {visible && (
        <Popper ref={setPopperElement} style={styles.popper} {...attributes.popper} theme={theme}>
          <Container unfixedWidth={unfixedWidth}>
            {!!title && (
              <Header>
                <Title>{title}</Title>
                <CloseButton
                  type="button"
                  onClick={() => setVisible(false)}
                  showOnDesktop={openOnClick}
                >
                  <Icon id="close_red" width={14} height={14} />
                </CloseButton>
              </Header>
            )}
            <Text size={size}>
              {content}
              {(openOnClick || isMobile) && !title && (
                <CloseButton type="button" onClick={() => setVisible(false)} showOnDesktop>
                  <Icon id="close_red" width={10} height={10} />
                </CloseButton>
              )}
            </Text>
          </Container>
          <Arrow ref={setArrowElement} style={styles.arrow} />
        </Popper>
      )}
    </Wrapper>
  )
}

const Tooltip: React.FC<TooltipProps & { enabled?: boolean }> = ({
  children,
  enabled = true,
  ...rest
}) => {
  if (!enabled) return <>{children}</>
  return <TooltipBase {...rest}>{children}</TooltipBase>
}

export default Tooltip
